/**
 * 微信扫码登录组件
 * 居中布局，扫码状态实时轮询
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, RefreshCw, AlertCircle, Check, Smartphone } from 'lucide-react';
import authService from '../../../services/authService';

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '';

const scanlineStyle = `
@keyframes scan {
    0%, 100% { transform: translateY(0); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(180px); opacity: 0; }
}
`;

const buildQrSourceList = (sessionData) => {
    if (!sessionData) return [];

    const ticket = sessionData.ticket || '';
    const sceneStr = sessionData.scene_str || '';
    const payload = encodeURIComponent(`contentos://wechat-login?ticket=${ticket}&scene_str=${sceneStr}`);

    return [
        sessionData.qr_url,
        `https://quickchart.io/qr?size=320&text=${payload}`,
        `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${payload}`,
    ].filter((item, index, arr) => Boolean(item) && arr.indexOf(item) === index);
};

const WechatQRCode = ({ onSuccess, onNeedBindPhone }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [session, setSession] = useState(null);
    const [status, setStatus] = useState('pending');
    const [userInfo, setUserInfo] = useState(null);
    const [qrSources, setQrSources] = useState([]);
    const [qrSourceIndex, setQrSourceIndex] = useState(0);

    const pollingTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    const clearPolling = useCallback(() => {
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    }, []);

    const startPolling = useCallback((ticket) => {
        clearPolling();
        const timer = setInterval(async () => {
            if (!isMountedRef.current) {
                clearPolling();
                return;
            }

            try {
                const data = await authService.checkWechatLoginStatus(ticket);
                if (!isMountedRef.current) return;

                if (data.status === 'scanned') {
                    setStatus('scanned');
                    setUserInfo({
                        nickname: data.nickname || data.wechat_nickname,
                        avatar: data.avatar || data.wechat_avatar,
                    });
                } else if (data.status === 'success') {
                    clearPolling();
                    setStatus('success');
                    onSuccess?.(data);
                } else if (data.status === 'expired') {
                    clearPolling();
                    setStatus('expired');
                    setError('二维码已过期');
                }
            } catch (err) {
                if (err.response?.data?.status === 'expired') {
                    clearPolling();
                    setStatus('expired');
                    setError(err.response?.data?.error || '二维码已过期或处理失败');
                }
            }
        }, 2000);

        pollingTimerRef.current = timer;
    }, [clearPolling, onSuccess, onNeedBindPhone]);

    const handleQrImageError = useCallback(() => {
        if (!isMountedRef.current) return;

        const nextIndex = qrSourceIndex + 1;
        if (nextIndex < qrSources.length) {
            setQrSourceIndex(nextIndex);
            return;
        }

        setError('二维码加载失败，请刷新二维码重试');
    }, [qrSourceIndex, qrSources]);

    const createSession = useCallback(async () => {
        if (!isMountedRef.current) return;

        setLoading(true);
        setError('');
        setStatus('pending');
        setQrSources([]);
        setQrSourceIndex(0);
        clearPolling();

        try {
            const data = await authService.createWechatLoginSession();
            if (!isMountedRef.current) return;

            setSession(data);
            const nextQrSources = buildQrSourceList(data);
            setQrSources(nextQrSources);
            setQrSourceIndex(0);
            startPolling(data.ticket);
        } catch (err) {
            if (isMountedRef.current) {
                setError(err.response?.data?.error || '获取二维码失败');
            }
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [clearPolling, startPolling]);

    useEffect(() => {
        isMountedRef.current = true;
        createSession();

        return () => {
            isMountedRef.current = false;
            clearPolling();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const activeQrSrc = qrSources[qrSourceIndex] || session?.qr_url || '';

    return (
        <>
            <style>{scanlineStyle}</style>

            <div className="flex flex-col items-center justify-center h-full">
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 p-2.5 mb-4 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600 w-full max-w-[200px]"
                    >
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{error}</span>
                    </motion.div>
                )}

                <div className="relative w-48 h-48 mb-4">
                    {loading ? (
                        <div className="w-full h-full bg-gray-50 rounded-xl flex items-center justify-center shadow-md">
                            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                        </div>
                    ) : status === 'expired' ? (
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={createSession}
                            className="w-full h-full bg-gray-50 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-all shadow-md"
                        >
                            <RefreshCw className="w-6 h-6 text-gray-400 mb-1.5" />
                            <span className="text-xs text-gray-500">点击刷新</span>
                        </motion.div>
                    ) : status === 'scanned' ? (
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="w-full h-full bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl flex flex-col items-center justify-center shadow-md border border-emerald-100"
                        >
                            {userInfo?.avatar ? (
                                <img src={userInfo.avatar} alt="avatar" className="w-14 h-14 rounded-full mb-2 shadow" />
                            ) : (
                                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
                                    <Check className="w-7 h-7 text-emerald-500" />
                                </div>
                            )}
                            <span className="text-sm font-bold text-emerald-600">{userInfo?.nickname || '已扫码'}</span>
                            <span className="text-xs text-emerald-500 mt-0.5">请在手机上确认</span>
                        </motion.div>
                    ) : status === 'success' ? (
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="w-full h-full bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl flex flex-col items-center justify-center shadow-md border border-emerald-100"
                        >
                            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
                                <Check className="w-7 h-7 text-emerald-500" />
                            </div>
                            <span className="text-sm font-bold text-emerald-600">登录成功</span>
                        </motion.div>
                    ) : (
                        <div className="relative w-full h-full bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-md border border-gray-100">
                            {activeQrSrc ? (
                                <>
                                    <img
                                        src={activeQrSrc}
                                        alt="wechat_qr_code"
                                        onError={handleQrImageError}
                                        className="w-full h-full object-contain p-2"
                                    />
                                    <div
                                        className="absolute left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent"
                                        style={{ animation: 'scan 2s ease-in-out infinite' }}
                                    />
                                </>
                            ) : (
                                <div className="text-center text-gray-400">
                                    <Loader2 className="w-6 h-6 mx-auto mb-1.5 animate-spin" />
                                    <p className="text-xs">加载中...</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {status === 'pending' && !loading && !error && (
                    <p className="text-sm text-gray-500 mb-3">
                        请使用 <span className="font-semibold" style={{ color: '#07C160' }}>微信</span> 扫码登录
                    </p>
                )}

                <div className="flex items-center gap-2 text-xs">
                    {!loading && status === 'pending' && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={createSession}
                            className="text-blue-500 hover:text-blue-600 flex items-center gap-1 transition-colors"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            刷新二维码
                        </motion.button>
                    )}

                    {!loading && status === 'pending' && session?.ticket && import.meta.env?.DEV && (
                        <>
                            <span className="text-gray-300">|</span>
                            <button
                                onClick={async () => {
                                    try {
                                        const resp = await fetch(`${API_BASE_URL}/api/v1/accounts/wechat/mock-scan/`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ ticket: session.ticket }),
                                        });
                                        if (resp.ok) console.log('[DEV] mock scan success');
                                    } catch (e) {
                                        console.error('[DEV] mock scan failed', e);
                                    }
                                }}
                                className="text-orange-500 hover:text-orange-600"
                            >
                                [DEV] 模拟扫码
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default WechatQRCode;
